import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment,
  IconButton, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Grid, CircularProgress, Alert,
  Snackbar, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fetchUsers, searchUsersByEmail, searchUserIdByEmail, getUserById, updateUser, deleteUser, AuthUser } from '../services/userService';

interface User extends AuthUser {
  // Enhanced user interface that extends the AuthUser interface
  status?: string; // Computed status based on disabled flag
  formattedLastLogin?: string;
  formattedCreatedAt?: string;
}

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Form state for editing user
  const [editForm, setEditForm] = useState({
    email: '',
    emailVerified: false,
    disabled: false,
    displayName: '',
    phoneNumber: ''
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  // No longer auto-searching when search term changes
  // Instead, only search when refresh button is clicked

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await fetchUsers(nextPageToken);
      
      const enhancedUsers = response.users.map(user => ({
        ...user,
        status: user.disabled ? 'disabled' : 'active',
        formattedLastLogin: user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleString() : 'Never',
        formattedCreatedAt: user.creationTime ? new Date(user.creationTime).toLocaleString() : 'Unknown'
      }));
      
      setUsers(prevUsers => nextPageToken ? [...prevUsers, ...enhancedUsers] : enhancedUsers);
      setFilteredUsers(prevUsers => nextPageToken ? [...prevUsers, ...enhancedUsers] : enhancedUsers);
      setNextPageToken(response.pageToken);
    } catch (error) {
      console.error("Error fetching users:", error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to load users', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearchUsers = async () => {
    try {
      setLoading(true);
      if (searchTerm.length > 0) {
        const trimmedSearch = searchTerm.trim();

        // Check if the input looks like a user ID (no @ symbol, alphanumeric)
        const isUserId = !trimmedSearch.includes('@');

        if (isUserId) {
          // Direct fetch by user ID
          console.log('Fetching user by ID:', trimmedSearch);
          try {
            const user = await getUserById(trimmedSearch);
            const enhancedUser = {
              ...user,
              status: user.disabled ? 'disabled' : 'active',
              formattedLastLogin: user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleString() : 'Never',
              formattedCreatedAt: user.creationTime ? new Date(user.creationTime).toLocaleString() : 'Unknown'
            };

            setFilteredUsers([enhancedUser]);
            setSnackbar({
              open: true,
              message: `Found user with ID "${trimmedSearch}"`,
              severity: 'success'
            });
          } catch (error) {
            console.error('User not found:', error);
            setFilteredUsers([]);
            setSnackbar({
              open: true,
              message: `No user found with ID "${trimmedSearch}"`,
              severity: 'error'
            });
          }
        } else {
          // Search by email in userDB
          console.log('Searching for user by email in userDB:', trimmedSearch);
          const userId = await searchUserIdByEmail(trimmedSearch);

          if (userId) {
            // Fetch the user data using the found user ID
            console.log('Found user ID in userDB:', userId);
            const user = await getUserById(userId);
            const enhancedUser = {
              ...user,
              status: user.disabled ? 'disabled' : 'active',
              formattedLastLogin: user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleString() : 'Never',
              formattedCreatedAt: user.creationTime ? new Date(user.creationTime).toLocaleString() : 'Unknown'
            };

            setFilteredUsers([enhancedUser]);
            setSnackbar({
              open: true,
              message: `Found user with email "${trimmedSearch}"`,
              severity: 'success'
            });
          } else {
            setFilteredUsers([]);
            setSnackbar({
              open: true,
              message: `No user found with email "${trimmedSearch}"`,
              severity: 'error'
            });
          }
        }
      } else {
        // If search is empty, show all users
        setFilteredUsers(users);
      }
    } catch (error) {
      console.error("Error searching users:", error);
      setSnackbar({
        open: true,
        message: 'Failed to search users',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      email: user.email || '',
      emailVerified: user.emailVerified || false,
      disabled: user.disabled || false,
      displayName: user.displayName || '',
      phoneNumber: user.phoneNumber || ''
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  // Handle text field changes
  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: value
    });
  };

  // Handle select changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: value
    });
  };
  
  // Toggle user active/disabled status
  const handleToggleUserStatus = async (user: User) => {
    try {
      const updatedUser = await updateUser(user.uid, {
        disabled: !user.disabled
      });
      
      // Enhance the user data
      const enhancedUser = {
        ...updatedUser,
        status: updatedUser.disabled ? 'disabled' : 'active',
        formattedLastLogin: updatedUser.lastSignInTime ? new Date(updatedUser.lastSignInTime).toLocaleString() : 'Never',
        formattedCreatedAt: updatedUser.creationTime ? new Date(updatedUser.creationTime).toLocaleString() : 'Unknown'
      };
      
      // Update local state
      const updatedUsers = users.map(u => 
        u.uid === user.uid ? enhancedUser : u
      );
      
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      
      setSnackbar({
        open: true,
        message: `User ${updatedUser.disabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error("Error toggling user status:", error);
      setSnackbar({
        open: true,
        message: 'Failed to update user status',
        severity: 'error'
      });
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;
    
    try {
      const updatedUser = await updateUser(selectedUser.uid, {
        email: editForm.email,
        emailVerified: editForm.emailVerified,
        disabled: editForm.disabled,
        displayName: editForm.displayName,
        phoneNumber: editForm.phoneNumber
      });
      
      // Enhance the user data
      const enhancedUser = {
        ...updatedUser,
        status: updatedUser.disabled ? 'disabled' : 'active',
        formattedLastLogin: updatedUser.lastSignInTime ? new Date(updatedUser.lastSignInTime).toLocaleString() : 'Never',
        formattedCreatedAt: updatedUser.creationTime ? new Date(updatedUser.creationTime).toLocaleString() : 'Unknown'
      };
      
      // Update local state
      const updatedUsers = users.map(user => 
        user.uid === selectedUser.uid 
          ? enhancedUser 
          : user
      );
      
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      
      setSnackbar({
        open: true,
        message: 'User updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error updating user:", error);
      setSnackbar({
        open: true,
        message: 'Failed to update user',
        severity: 'error'
      });
    } finally {
      setEditDialogOpen(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    
    try {
      await deleteUser(selectedUser.uid);
      
      // Update local state
      const updatedUsers = users.filter(user => user.uid !== selectedUser.uid);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        open: true,
        message: 'Failed to delete user',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>
      
      {/* Search and Filter */}
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by user ID or email address"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleSearchUsers}>
              Search
            </Button>
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={fetchUserData}
              sx={{ ml: 1 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Users Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>UID</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Management</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <TableRow key={user.uid}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {user.uid.substring(0, 6) + '...'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.email || 'N/A'}
                      {user.emailVerified && (
                        <Chip 
                          size="small" 
                          label="Verified" 
                          color="success" 
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                        />
                      )}
                    </TableCell>
                    <TableCell>{user.displayName || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={user.disabled ? 'Disabled' : 'Active'} 
                        color={user.disabled ? 'error' : 'success'} 
                      />
                    </TableCell>
                    <TableCell>{user.formattedCreatedAt}</TableCell>
                    <TableCell>{user.formattedLastLogin}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => navigate(`/users/${user.uid}`)}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>
      
      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box my={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Display Name"
                  name="displayName"
                  value={editForm.displayName}
                  onChange={handleTextFieldChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={editForm.email}
                  onChange={handleTextFieldChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={editForm.phoneNumber}
                  onChange={handleTextFieldChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Account Status</InputLabel>
                  <Select
                    name="disabled"
                    value={editForm.disabled ? 'true' : 'false'}
                    label="Account Status"
                    onChange={(e) => {
                      setEditForm({
                        ...editForm,
                        disabled: e.target.value === 'true'
                      });
                    }}
                  >
                    <MenuItem value="false">Active</MenuItem>
                    <MenuItem value="true">Disabled</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Email Verified</InputLabel>
                  <Select
                    name="emailVerified"
                    value={editForm.emailVerified ? 'true' : 'false'}
                    label="Email Verified"
                    onChange={(e) => {
                      setEditForm({
                        ...editForm,
                        emailVerified: e.target.value === 'true'
                      });
                    }}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleEditSubmit} color="primary" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete user {selectedUser?.displayName || selectedUser?.email}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
